import { useState } from "react";

const FORM_ADDR = "https://readdy.ai/api/form/dagmfa9tto0ceqqnhvog";

const inputCls =
  "w-full rounded-md border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-800 outline-none transition placeholder:text-foreground-400 focus:border-primary-300 focus:ring-2 focus:ring-primary-100";

export default function ContactForm() {
  const [status, setStatus] = useState<"idle" | "ok" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [msgLen, setMsgLen] = useState(0);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = String(formData.get("company_alt") ?? "").trim();

    // honeypot hit -> pretend success so bots don't get a clear rejection
    if (honeypot) {
      form.reset();
      setStatus("ok");
      return;
    }
    formData.delete("company_alt");

    setSubmitting(true);
    setStatus("idle");
    setFormError("");
    try {
      const body = new URLSearchParams(formData as unknown as URLSearchParams).toString();
      const res = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const meta = parsed?.meta as Record<string, unknown> | undefined;
      const serverMsg =
        meta?.message || parsed?.message || meta?.detail || responseText || "";
      const code = parsed?.code;
      const isSpam =
        String(serverMsg).toLowerCase().includes("spam") ||
        String(serverMsg).includes("form data is spam");

      if (res.ok && code === "OK" && !isSpam) {
        form.reset();
        setMsgLen(0);
        setStatus("ok");
        setFormError("");
      } else {
        setStatus("error");
        setFormError(
          String(serverMsg) && serverMsg !== "OK"
            ? String(serverMsg)
            : "提交未成功，请稍后重试或更换联系方式后再试。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，未能提交。请检查网络后重试。");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section id="contact" className="scroll-mt-24">
      <div className="mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-16">
        <div className="overflow-hidden rounded-2xl border border-background-200 bg-background-50">
          <div className="grid lg:grid-cols-2">
            {/* left info */}
            <div className="flex flex-col justify-between bg-primary-600 p-7 text-background-50 md:p-9">
              <div>
                <div className="mb-3 flex items-center gap-2">
                  <i className="ri-megaphone-line text-xl" />
                  <span className="font-en text-xs uppercase tracking-[0.28em] text-primary-100">
                    Send Us a Tip
                  </span>
                </div>
                <h2 className="font-heading text-2xl font-bold leading-tight md:text-3xl">
                  爆料投稿 · 联系我们
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-primary-100 md:text-[15px]">
                  有独家线索、靠谱信源，或想对我们核验过的瓜进行补充？欢迎投稿。我们承诺在保密前提下审核每一条来稿，重要线索将转入事实核查流程。
                </p>
                <ul className="mt-6 space-y-3 text-sm text-primary-100">
                  <li className="flex items-start gap-3">
                    <i className="ri-focus-3-line mt-0.5" />
                    <span>尽量附上可核验的线索来源或凭证方向，别直接散播未经确认的个人隐私。</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-time-line mt-0.5" />
                    <span>编辑部将在 1–3 个工作日内评估，选中线索会通过你留下的联系方式沟通。</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-shield-check-line mt-0.5" />
                    <span>匿名或实名均可，是否公开你的来稿身份完全由你决定。</span>
                  </li>
                </ul>
              </div>
              <p className="mt-8 inline-flex items-center gap-2 text-sm text-primary-100">
                <i className="ri-mail-line" /> gua@meiridagua.example.com
              </p>
            </div>

            {/* right form */}
            <form
              onSubmit={handleSubmit}
              data-readdy-form="true"
              className="space-y-4 p-7 md:p-9"
              noValidate
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="cf-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    你的称呼
                  </label>
                  <input id="cf-name" name="name" type="text" placeholder="怎么称呼你" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="cf-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    联系邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="cf-email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于编辑部回访"
                    className={inputCls}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="cf-subject" className="mb-1.5 block text-sm font-medium text-foreground-800">
                  爆料标题 <span className="text-primary-500">*</span>
                </label>
                <input
                  id="cf-subject"
                  name="subject"
                  type="text"
                  required
                  maxLength={60}
                  placeholder="一句话概括线索核心"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="cf-message" className="mb-1.5 flex items-center justify-between text-sm font-medium text-foreground-800">
                  <span>
                    爆料内容 <span className="text-primary-500">*</span>
                  </span>
                  <span className="text-xs font-normal text-foreground-400">{msgLen}/500</span>
                </label>
                <textarea
                  id="cf-message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  placeholder="请尽量完整地描述线索的时间、主体与信源方向…"
                  className={`${inputCls} resize-none`}
                  onChange={(e) => setMsgLen(e.target.value.length)}
                />
              </div>

              {/* honeypot (hidden via stylesheet) */}
              <input
                name="company_alt"
                type="text"
                autoComplete="off"
                aria-hidden="true"
                readOnly
                tabIndex={-1}
                className="lead-gate"
              />

              {status === "ok" ? (
                <div className="flex items-start gap-2 rounded-md border border-background-200 bg-background-100 px-4 py-3 text-sm text-foreground-700">
                  <i className="ri-checkbox-circle-line mt-0.5 text-lg text-accent-600" />
                  已收到你的投稿！我们会尽快审核，若线索进入流程将与你联系。
                </div>
              ) : null}
              {status === "error" ? (
                <div className="flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-sm text-primary-800">
                  <i className="ri-error-warning-line mt-0.5 text-lg" />
                  {formError}
                </div>
              ) : null}

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-6 py-3 text-sm font-semibold text-background-50 transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" /> 提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line" /> 提交爆料
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}